import React, { useEffect, useState } from "react";
import "../styles/ProfessionalInfo.css";

const calcGst = (amount, gstType) => {
  const amt = parseFloat(amount);
  if (!amt || amt <= 0) return null;
  if (gstType === "exclusive") {
    const gstAmount = parseFloat((amt * 0.10).toFixed(2));
    const baseAmount = parseFloat(amt.toFixed(2));
    return { baseAmount, gstAmount, totalPayable: parseFloat((baseAmount + gstAmount).toFixed(2)) };
  }
  // inclusive
  const baseAmount = parseFloat((amt / 1.10).toFixed(2));
  const gstAmount = parseFloat((amt - baseAmount).toFixed(2));
  return { baseAmount, gstAmount, totalPayable: parseFloat(amt.toFixed(2)) };
};

const ProfessionalInfo = ({ onNext, onPrevious, initialData = {} }) => {
  const isContractor =
    (initialData.memberType || "").toLowerCase() === "contractor" ||
    (initialData.role || "").toLowerCase() === "contractor";

  const [data, setData] = useState({
    jobTitle: initialData.jobTitle || "",
    payType: initialData.payType || "",
    perHourRate: initialData.perHourRate || "",
    gstType: initialData.gstType || "exclusive",
    superAnnuation: initialData.superAnnuation || "11",
    salaryTerm: initialData.salaryTerm || "",
    typeOfWork: initialData.typeOfWork || "",
    paydayDay: initialData.paydayDay || "",
    paydayTime: initialData.paydayTime || "09:00",
    joiningDate: initialData.joiningDate || "",
    bonus: initialData.bonus || "",
    allowance: initialData.allowance || "",
    commission: initialData.commission || "",
  });

  const [gstBreakdown, setGstBreakdown] = useState(null);

  useEffect(() => {
    if (!isContractor) return;
    setGstBreakdown(calcGst(data.perHourRate, data.gstType));
  }, [data.perHourRate, data.gstType, isContractor]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    onNext(data);
  };

  return (
    <div className="professional-info">
      {!isContractor && (
        <div className="form-row">
          <label>Level</label>
          <select
            name="jobTitle"
            value={data.jobTitle}
            onChange={handleChange}
            required
          >
            <option value="">Select Level</option>
            <option value="Junior">Junior</option>
            <option value="Intermediate">Intermediate</option>
            <option value="Senior">Senior</option>
          </select>
        </div>
      )}

      {isContractor && (
        <div className="form-row">
          <label>Pay Type</label>
          <select
            name="payType"
            value={data.payType}
            onChange={handleChange}
            required
          >
            <option value="">Select Pay Type</option>
            <option value="pay-per-hour">Pay per hour</option>
            <option value="pay-per-project">Pay per project</option>
          </select>
        </div>
      )}

      <div className="form-row">
        <label>{isContractor ? "Rate" : "Per Hour Rate"}</label>
        <input
          type="number"
          name="perHourRate"
          value={data.perHourRate}
          onChange={handleChange}
          placeholder="Enter Rate"
          required
        />
      </div>

      {isContractor && (
        <div className="form-row">
          <label>GST Type</label>
          <select
            name="gstType"
            value={data.gstType}
            onChange={handleChange}
          >
            <option value="exclusive">GST Exclusive</option>
            <option value="inclusive">GST Inclusive</option>
          </select>
        </div>
      )}

      {isContractor && gstBreakdown && (
        <div className="gst-breakdown">
          <div className="gst-breakdown-title">
            GST Breakdown
            <span className="gst-type-badge">
              {data.gstType === "exclusive" ? "Exclusive — GST added on top" : "Inclusive — GST extracted from amount"}
            </span>
          </div>
          <div className="gst-breakdown-rows">
            <div className="gst-row">
              <span>Base Amount</span>
              <span>${gstBreakdown.baseAmount.toFixed(2)}</span>
            </div>
            <div className="gst-row">
              <span>GST (10%)</span>
              <span>${gstBreakdown.gstAmount.toFixed(2)}</span>
            </div>
            <div className="gst-row total">
              <span>Total Payable</span>
              <span>${gstBreakdown.totalPayable.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {!isContractor && (
        <div className="form-row">
          <label>Super Annuation</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="number"
              name="superAnnuation"
              value={data.superAnnuation}
              onChange={handleChange}
              placeholder="11"
              style={{ flex: 1 }}
              required
            />
            <span>%</span>
          </div>
        </div>
      )}

      {!isContractor && (
        <div className="form-row">
          <label>Salary Term</label>
          <select
            name="salaryTerm"
            value={data.salaryTerm}
            onChange={handleChange}
            required
          >
            <option value="">Select Salary Term</option>
            <option value="hourly">Hourly</option>
            <option value="weekly">Weekly</option>
            <option value="fortnightly">Fortnightly</option>
            <option value="monthly">Monthly</option>
            <option value="annually">Annually</option>
          </select>
        </div>
      )}

      {!isContractor && (
        <div className="form-row">
          <label>Type of Work</label>
          <select
            name="typeOfWork"
            value={data.typeOfWork}
            onChange={handleChange}
            required
          >
            <option value="">Select Type of Work</option>
            <option value="casual">Casual</option>
            <option value="part-time">Part-time</option>
            <option value="full-time">Full-time</option>
            <option value="cash">Cash</option>
          </select>
        </div>
      )}

      {!isContractor && (
        <div className="form-row">
          <label>Bonus ($)</label>
          <input
            type="number"
            name="bonus"
            value={data.bonus}
            onChange={handleChange}
            placeholder="0.00"
            min="0"
          />
        </div>
      )}

      {!isContractor && (
        <div className="form-row">
          <label>Allowance ($)</label>
          <input
            type="number"
            name="allowance"
            value={data.allowance}
            onChange={handleChange}
            placeholder="0.00"
            min="0"
          />
        </div>
      )}

      {!isContractor && (
        <div className="form-row">
          <label>Commission ($)</label>
          <input
            type="number"
            name="commission"
            value={data.commission}
            onChange={handleChange}
            placeholder="0.00"
            min="0"
          />
        </div>
      )}

      <div className="form-row">
        <label>Payday</label>
        <div className="payday-row">
          <select
            name="paydayDay"
            value={data.paydayDay}
            onChange={handleChange}
            required
          >
            <option value="">Select Day</option>
            <option value="sunday">Sunday</option>
            <option value="monday">Monday</option>
            <option value="tuesday">Tuesday</option>
            <option value="wednesday">Wednesday</option>
            <option value="thursday">Thursday</option>
            <option value="friday">Friday</option>
            <option value="saturday">Saturday</option>
          </select>
          <input
            type="time"
            name="paydayTime"
            value={data.paydayTime}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="form-row">
        <label>Joining Date</label>
        <input
          type="date"
          name="joiningDate"
          value={data.joiningDate}
          onChange={handleChange}
          required
        />
      </div>

      <div className="form-buttons">
        <button className="professional-back-btn" onClick={onPrevious}>
          Back
        </button>
        <button className="professional-next-btn" onClick={handleNext}>
          Next
        </button>
      </div>
    </div>
  );
};

export default ProfessionalInfo;
